import { Fragment, useState, useContext } from 'react';
import { Dialog, Transition, Menu } from '@headlessui/react';
import { Link, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  Bars3Icon,
  XMarkIcon,
  HomeIcon,
  FolderIcon,
  DocumentTextIcon,
  BookOpenIcon,
  UsersIcon,
  CogIcon,
  UserCircleIcon,
  ArrowRightOnRectangleIcon
} from '@heroicons/react/24/outline';
import AuthContext from '../context/AuthContext';

const navigation = [
  { name: 'Dashboard', href: '/', icon: HomeIcon },
  { name: 'Categories', href: '/categories', icon: FolderIcon },
  { name: 'Subcategories', href: '/subcategories', icon: DocumentTextIcon },
  { name: 'Posts', href: '/posts', icon: BookOpenIcon },
  { name: 'Users', href: '/users', icon: UsersIcon },
  { name: 'Settings', href: '/settings', icon: CogIcon },
];

export default function Layout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = () => {
    setShowLogoutDialog(true);
  };

  const confirmLogout = () => {
    // Show logout toast
    toast.success('Logged out successfully!');
    
    // Logout and navigate
    logout();
    navigate('/login');
    setShowLogoutDialog(false);
  };

  const cancelLogout = () => {
    setShowLogoutDialog(false);
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Mobile sidebar */}
      <Transition.Root show={sidebarOpen} as={Fragment}>
        <Dialog as="div" className="relative z-40 md:hidden" onClose={setSidebarOpen}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-600 bg-opacity-75" />
          </Transition.Child>

          <div className="fixed inset-0 z-40 flex">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="-translate-x-full"
              enterTo="translate-x-0"
              leave="ease-in duration-200"
              leaveFrom="translate-x-0"
              leaveTo="-translate-x-full"
            >
              <Dialog.Panel className="relative flex w-full max-w-sm flex-1 flex-col" style={{ backgroundColor: '#212121' }}>
                <div className="flex flex-shrink-0 items-center justify-between px-6 py-4">
                  <div className="flex items-center">
                    <div className="w-12 h-12 rounded-full flex items-center justify-center mr-4 overflow-hidden" style={{ backgroundColor: '#1E605B' }}>
                      <img 
                        src="/img/logo.png" 
                        alt="Lekhan Mala Logo" 
                        className="w-full h-full object-contain"
                      />
                    </div>
                    <h1 className="text-2xl text-white">Lekhan Mala</h1>
                  </div>
                  <button
                    type="button"
                    className="focus:outline-none"
                    onClick={() => setSidebarOpen(false)}
                  >
                    <span className="sr-only">Close sidebar</span>
                    <XMarkIcon className="h-6 w-6 text-gray-300 hover:text-white" aria-hidden="true" />
                  </button>
                </div>
                <div className="mt-8 h-0 flex-1 overflow-y-auto">
                  <nav className="space-y-2 px-4">
                    {navigation.map((item) => {
                      const isActive = location.pathname === item.href;
                      return (
                        <Link
                          key={item.name}
                          to={item.href}
                          onClick={(e) => {
                            e.preventDefault();
                            console.log('Navigation clicked:', item.name, item.href);
                            setSidebarOpen(false);
                            // Use setTimeout to ensure the sidebar closes before navigation
                            setTimeout(() => {
                              navigate(item.href);
                            }, 100);
                          }}
                          className={`group flex items-center px-4 py-3 text-lg font-medium rounded-md text-white hover:bg-gray-600 ${
                            isActive ? 'bg-gray-600' : ''
                          }`}
                        >
                          <item.icon className="mr-4 h-7 w-7 flex-shrink-0" aria-hidden="true" />
                          {item.name}
                        </Link>
                      );
                    })}
                  </nav>
                </div>
              </Dialog.Panel>
            </Transition.Child>
            <div className="w-14 flex-shrink-0" aria-hidden="true">
              {/* Force sidebar to shrink to fit close icon */}
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Static sidebar for desktop */}
      <div className="hidden md:flex md:w-64 md:flex-col md:fixed md:inset-y-0">
        <div className="flex flex-col flex-grow pt-6 overflow-y-auto" style={{ backgroundColor: '#212121' }}>
          <div className="flex items-center flex-shrink-0 px-6">
            <div className="flex items-center">
              <div className="w-12 h-12 rounded-full flex items-center justify-center mr-4 overflow-hidden" style={{ backgroundColor: '#1E605B' }}>
                <img 
                  src="/img/logo.png" 
                  alt="Lekhan Mala Logo" 
                  className="w-full h-full object-contain"
                />
              </div>
              <h1 className="text-2xl text-white">Lekhan Mala</h1>
            </div>
          </div>
          <div className="mt-8 flex-grow flex flex-col">
            <nav className="flex-1 px-4 pb-4 space-y-2">
              {navigation.map((item) => {
                const isActive = location.pathname === item.href;
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`group flex items-center px-4 py-3 text-lg font-medium rounded-md text-white hover:bg-gray-600 ${
                      isActive ? 'bg-gray-600' : ''
                    }`}
                  >
                    <item.icon className="mr-4 h-7 w-7 flex-shrink-0" aria-hidden="true" />
                    {item.name}
                  </Link>
                );
              })}
            </nav>
          </div>
        </div>
      </div>

      <div className="md:pl-64 flex flex-col flex-1">
        {/* Top header */}
        <div className="sticky top-0 z-10 shadow" style={{ backgroundColor: '#212121' }}>
          <div className="flex items-center justify-between px-6 py-4">
            <div className="flex items-center">
              <button
                type="button"
                className="md:hidden -ml-0.5 -mt-0.5 h-12 w-12 inline-flex items-center justify-center rounded-md text-gray-300 hover:text-white focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500"
                onClick={() => setSidebarOpen(true)}
              >
                <span className="sr-only">Open sidebar</span>
                <Bars3Icon className="h-6 w-6" aria-hidden="true" />
              </button>
              <div className="hidden md:flex items-center">
                <span className="text-white text-xl font-semibold">A Literature App Admin Panel</span>
              </div>
            </div>
            <div className="flex items-center">
              <Menu as="div" className="relative ml-3">
                <div>
                  <Menu.Button className="flex max-w-xs items-center rounded-full bg-gray-600 text-sm focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-gray-800">
                    <span className="sr-only">Open user menu</span>
                    <div className="w-12 h-12 rounded-full overflow-hidden">
                      <img 
                        src="/img/admin_image.png" 
                        alt="Admin Profile" 
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </Menu.Button>
                </div>
                <Transition
                  as={Fragment}
                  enter="transition ease-out duration-100"
                  enterFrom="transform opacity-0 scale-95"
                  enterTo="transform opacity-100 scale-100"
                  leave="transition ease-in duration-75"
                  leaveFrom="transform opacity-100 scale-100"
                  leaveTo="transform opacity-0 scale-95"
                >
                  <Menu.Items className="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                    <Menu.Item>
                      {({ active }) => (
                        <Link
                          to="/profile"
                          className={`${
                            active ? 'bg-gray-100' : ''
                          } block px-4 py-3 text-base text-gray-700 flex items-center`}
                        >
                          <UserCircleIcon className="h-5 w-5 mr-2" />
                          Profile
                        </Link>
                      )}
                    </Menu.Item>
                    <Menu.Item>
                      {({ active }) => (
                        <button
                          onClick={handleLogout}
                          className={`${
                            active ? 'bg-gray-100' : ''
                          } block w-full text-left px-4 py-3 text-base text-gray-700 flex items-center`}
                        >
                          <ArrowRightOnRectangleIcon className="h-5 w-5 mr-2" />
                          Logout
                        </button>
                      )}
                    </Menu.Item>
                  </Menu.Items>
                </Transition>
              </Menu>
            </div>
          </div>
        </div>

        <main className="flex-1 bg-gray-100">
          <div className="py-6">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
              <Outlet />
            </div>
          </div>
        </main>
      </div>

      {/* Logout Confirmation Dialog */}
      <Transition.Root show={showLogoutDialog} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={setShowLogoutDialog}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-96 sm:h-80">
                  <div className="bg-white px-6 py-8 h-full flex flex-col justify-center">
                    <div className="text-center">
                      <div className="mx-auto flex h-16 w-16 flex-shrink-0 items-center justify-center rounded-full bg-gray-100 mb-6">
                        <svg className="h-8 w-8 text-gray-600" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                        </svg>
                      </div>
                      <Dialog.Title as="h3" className="text-2xl font-semibold leading-6 text-gray-900 mb-6">
                        Logout?
                      </Dialog.Title>
                      <p className="text-lg text-gray-500 mb-8">
                        Are you sure you want to logout?
                      </p>
                      <div className="flex gap-4 justify-center">
                        <button
                          type="button"
                          className="inline-flex justify-center rounded-md bg-red-600 px-8 py-3 text-lg font-semibold text-white shadow-sm hover:bg-red-500"
                          onClick={confirmLogout}
                        >
                          Yes, Logout
                        </button>
                        <button
                          type="button"
                          className="inline-flex justify-center rounded-md bg-white px-8 py-3 text-lg font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                          onClick={cancelLogout}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </div>
  );
}